import { makeid } from 'utils/helpers/makeid';

import {
  booleanOptions,
  notEscalatedOptions,
  nursingInterventionOptions,
  stringBooleanOptions,
  commonFactors,
} from './common';

import type { TFormItem, IFormItemOption } from '../types';

export const hypervolemiaSymptoms: IFormItemOption<string>[] = [
  {
    label: 'Pink or blood tinged sputum',
    value: 'pinkOrBloodSputum',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Shortness of breath',
    value: 'shortnessOfBreath',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Chest pain',
    value: 'chestPain',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Dizziness/Lightheadedness or confusion',
    value: 'dizziness',
    required: true,
    key: makeid(10),
  },
  {
    label: 'Dry hacking cough',
    value: 'cough',
    key: makeid(10),
  },
  {
    label: 'Wet productive cough',
    value: 'wetCough',
    key: makeid(10),
  },
  {
    label: 'Swelling in the feet, ankles or legs',
    value: 'swellingFeet',
    key: makeid(10),
  },

  {
    label: 'Difficulty laying flat',
    value: 'difficultyLayingFlat',
    key: makeid(10),
  },
  {
    label: 'Loss of appetite',
    value: 'lossOfAppetite',
    key: makeid(10),
  },
] as const;

export const factorsAssessment: IFormItemOption<string>[] = [
  ...commonFactors,
  {
    label: 'Using the bathroom before stepping on the scale',
    value: 'usingTheBathroom',
    key: makeid(10),
  },
  {
    label: 'Wearing light/similar clothing on the scale',
    value: 'wearingClothing',
    key: makeid(10),
  },
  {
    label: 'Wearing shoes on the scale',
    value: 'wearingShoes',
    key: makeid(10),
  },
  {
    label: 'Eating/drinking before stepping on the scale',
    value: 'eatingDrinking',
    key: makeid(10),
  },
  {
    label: 'Measuring at roughly the same time every day',
    value: 'measuringAtRoughly',
    key: makeid(10),
  },
  {
    label: 'Someone else used my scale',
    value: 'someoneElseUsedScale',
    key: makeid(10),
  },
];

export const assessmentChfWeightForm: TFormItem[] = [
  {
    key: makeid(10),
    title: '#1 Did you speak to patient?',
    description:
      'Choose other if unlisted family member & type in their relationship. (Ex: Daughter in law) Add Name of Individual you spoke to in other.',
    type: 'radio',
    required: true,
    name: 'patient',
    options: [
      { value: 'Patient', label: 'Patient', key: makeid(10) },
      { value: 'Spouse', label: 'Spouse', key: makeid(10) },
      { value: 'Caregiver', label: 'Caregiver', key: makeid(10) },
      {
        value: 'Emergency Contacts',
        label: 'Emergency Contacts',
        key: makeid(10),
      },
      { value: null, label: 'Other', key: makeid(10) },
    ],
    extra: [
      {
        type: 'doubleRowInput',
        name: 'other',
        key: makeid(10),
        options: [
          { label: 'Other Member', value: 'other', key: makeid(10) },
          { label: 'Relationship', value: 'relationship', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#2 Mental Status Assessment',
    description:
      'Oriented and Coherent 1: Person 2: Person, place 3: Person, place, time 4: Person, place, time, situation?',
    type: 'radio',
    required: false,
    name: 'AOC',
    options: [
      { value: 'x1', label: 'x1', key: makeid(10) },
      { value: 'x2', label: 'x2', key: makeid(10) },
      { value: 'x3', label: 'x3', key: makeid(10) },
      { value: 'x4', label: 'x4', key: makeid(10) },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#3 Appointments',
    type: 'datepicker',
    name: 'datepicker',
    required: false,
    options: [
      {
        value: 'last',
        label: "Last Doctor's Appointment",
        type: 'max',
        key: makeid(10),
      },
      {
        value: 'next',
        label: "Next Doctor's Appointment",
        type: 'min',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#4 Patient is able to take another reading at time of call?',
    subtitle: 'Is the reading still out of bounds?',
    type: 'radio',
    required: true,
    name: 'isOneMoreReading',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'radio',
        name: 'bounds',
        label: 'Is the reading still out of bounds?',
        key: makeid(10),
        options: booleanOptions,
        divider: true,
      },
      {
        type: 'input',
        name: 'repeatReadingWas',
        key: makeid(10),
        options: [
          {
            label: 'The repeat reading was:',
            value: 'repeatReadingWas',
            key: makeid(10),
          },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#5 Signs and Symptoms of Congestive Heart Failure',
    type: 'radio-list',
    name: 'symptoms',
    required: true,
    options: hypervolemiaSymptoms,
    extra: [
      {
        name: 'symptomsOnset',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#6 Did patient take routine medications as prescribed?',
    type: 'radio',
    required: false,
    name: 'isTakeMedications',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'checkbox',
        name: 'prescribed',
        key: makeid(10),
        options: [
          {
            value: 'bloodPressureMedication',
            label: 'Blood pressure medication',
            key: makeid(10),
          },
          { value: 'diuretic', label: 'Diuretic', key: makeid(10) },
          {
            value: 'antiArrhythmic',
            label: 'Anti-arrhythmic medication',
            key: makeid(10),
          },
        ],
      },
      {
        type: 'input',
        name: 'otherPrescribed',
        key: makeid(10),
        options: [
          { label: 'Other', value: 'otherPrescribed', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#7 Did patient take any prescribed PRN medications?',
    type: 'radio',
    required: false,
    name: 'isTakePrnMedications',
    options: stringBooleanOptions,
    extra: [
      {
        type: 'checkbox',
        name: 'prnPrescribed',
        key: makeid(10),
        options: [
          {
            value: 'bloodPressureMedication',
            label: 'Blood pressure medication',
            key: makeid(10),
          },
          { value: 'diuretic', label: 'Diuretic', key: makeid(10) },
          {
            value: 'antiArrhythmic',
            label: 'Anti-arrhythmic medication',
            key: makeid(10),
          },
        ],
      },
      {
        type: 'input',
        name: 'otherPrnMedications',
        key: makeid(10),
        options: [
          { label: 'Other', value: 'otherPrnMedications', key: makeid(10) },
        ],
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#8 Factors Assessment',
    type: 'radio-list',
    required: false,
    options: factorsAssessment,
    name: 'factorsAssessment',
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#9 Nursing Intervention',
    type: 'checkbox',
    name: 'nursingIntervention',
    required: true,
    options: nursingInterventionOptions,
    extra: [
      {
        type: 'labelCheckbox',
        name: 'notEscalated',
        key: makeid(10),
        label: 'Not Escalated',
        options: notEscalatedOptions,
        divider: true,
      },
      {
        name: 'interventionDetails',
        type: 'text',
        key: makeid(10),
      },
    ],
    readingTypeFor: ['min', 'max'],
  },
  {
    key: makeid(10),
    title: '#10 Patient Understanding',
    description:
      'Patient verbalized understanding and is agreeable to nursing recommendation?',
    type: 'radio',
    required: false,
    name: 'verbalized',
    options: booleanOptions,
    readingTypeFor: ['min', 'max'],
  },
];
